/**
 * Pure formatting helpers for activity/flow process logs.
 *
 * These live outside flow_log.service.ts so they can be unit tested directly: importing the service
 * pulls in @/core/api and, through it, the router's .vue imports, which the ts-jest transform cannot
 * resolve. flow_log.service.ts re-exports everything here, so existing import paths keep working.
 */

export function cleanEndings(arr: string[]): string[] {
  if (!Array.isArray(arr)) return [];
  // Only trailing newlines. This used to also strip ")" to shed the toString() wrapper's closing
  // paren (AI-274); now that processLog is plain text, doing so would eat real log content.
  return arr.map((item) =>
    typeof item === "string" ? item.replace(/\n*$/, "") : ""
  );
}

export function clearLogs(stepLogs: Array<{ processLog?: string }>): string[] {
  if (!Array.isArray(stepLogs)) return [];
  // AI-274: processLog is the log text. It used to arrive wrapped in Lombok's toString()
  // ("StepProcessLog(id=null, log=…)"), which this unwrapped by splitting on "log=" — that also
  // truncated any line containing that substring, and empty logs had to be spotted by matching
  // the literal "Log(id=null, log=)" marker.
  const logArray = stepLogs.map((entry) => entry?.processLog ?? "");
  return cleanEndings(logArray.filter((item) => item.trim() !== ""));
}

/**
 * Build the lines rendered in an activity's process-log panel: the step process logs, followed by a
 * "Process Error Logs" section when the activity carries flow-level error logs.
 *
 * The error logs are merged even when there are no step logs at all. A run that fails or is skipped
 * before any step executes stores only errorLogs — FlowJob.skipScheduledFire saves a SKIPPED FlowLog
 * whose only payload is the skip reason — so gating the merge on step logs hid the one piece of
 * diagnostic text those activities have.
 */
export function buildActivityLogLines(
  stepLogs: Array<{ processLog?: string }> | null | undefined,
  errorLogs: Array<{ log?: string }> | null | undefined
): string[] {
  const lines = clearLogs(Array.isArray(stepLogs) ? stepLogs : []);
  const errorMessages = (Array.isArray(errorLogs) ? errorLogs : [])
    .map((entry) => entry?.log)
    .filter((log): log is string => Boolean(log));
  if (errorMessages.length > 0) {
    lines.push("------- Process Error Logs -------");
    lines.push(...errorMessages);
  }
  return lines;
}

export function splitByTime(str: string): string[] {
  if (!str) return [];
  return str
    .split(/(?=\b\d{2}:\d{2}:\d{2}\b)/)
    .map((s) => s.replace(/\n*$/, "").trim())
    .filter(Boolean);
}
